export type CustomCodeHighlightMode = 'html-mixed' | 'css' | 'js';

type TokenType = 'comment' | 'string' | 'keyword' | 'number' | 'property' | 'tagName' | 'attrName' | 'punctuation';

const TOKEN_CLASS_BY_TYPE: Record<TokenType, string> = {
  comment: 'custom-code-editor__token--comment',
  string: 'custom-code-editor__token--string',
  keyword: 'custom-code-editor__token--keyword',
  number: 'custom-code-editor__token--number',
  property: 'custom-code-editor__token--property',
  tagName: 'custom-code-editor__token--tag-name',
  attrName: 'custom-code-editor__token--attr-name',
  punctuation: 'custom-code-editor__token--punctuation'
};

const JS_KEYWORDS = [
  'as', 'async', 'await', 'break', 'case', 'catch', 'class', 'const', 'continue', 'default', 'delete',
  'do', 'else', 'export', 'extends', 'false', 'finally', 'for', 'from', 'function', 'if', 'import',
  'in', 'instanceof', 'let', 'new', 'null', 'of', 'return', 'static', 'super', 'switch', 'this',
  'throw', 'true', 'try', 'typeof', 'undefined', 'var', 'void', 'while', 'with', 'yield'
];

const JS_TOKEN_PATTERN = new RegExp(
  String.raw`/\*[\s\S]*?\*/|//[^\n\r]*|"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|\`(?:\\.|[^\`\\])*\`|\b(?:${JS_KEYWORDS.join('|')})\b|\b\d+(?:\.\d+)?\b`,
  'g'
);

const CSS_TOKEN_PATTERN = /\/\*[\s\S]*?\*\/|"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|@[a-zA-Z-]+|#[\da-fA-F]{3,8}\b|\b\d+(?:\.\d+)?(?:px|rem|em|%|vh|vw|s|ms|deg)?\b|\b[a-zA-Z-]+(?=\s*:)|\b!important\b|[{}:;(),]/g;

const HTML_TOKEN_PATTERN = /<!--[\s\S]*?-->|<\/?[a-zA-Z][\s\S]*?>/g;
const SCRIPT_BLOCK_PATTERN = /<script\b[^>]*>[\s\S]*?<\/script\s*>/gi;

const escapeHtml = (value: string): string => value
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;')
  .replaceAll("'", '&#39;');

const wrapToken = (value: string, tokenType: TokenType): string => {
  if (value.length === 0) {
    return '';
  }

  return `<span class="${TOKEN_CLASS_BY_TYPE[tokenType]}">${escapeHtml(value)}</span>`;
};

const highlightByPattern = (
  value: string,
  pattern: RegExp,
  tokenTypeResolver: (token: string) => TokenType | null
): string => {
  let highlighted = '';
  let currentIndex = 0;
  pattern.lastIndex = 0;

  let match = pattern.exec(value);

  while (match) {
    const [token] = match;
    const index = match.index;

    highlighted += escapeHtml(value.slice(currentIndex, index));

    const tokenType = tokenTypeResolver(token);
    highlighted += tokenType ? wrapToken(token, tokenType) : escapeHtml(token);

    currentIndex = index + token.length;
    match = pattern.exec(value);
  }

  highlighted += escapeHtml(value.slice(currentIndex));
  return highlighted;
};

const highlightJavaScript = (value: string): string => highlightByPattern(
  value,
  JS_TOKEN_PATTERN,
  (token) => {
    if (token.startsWith('/*') || token.startsWith('//')) {
      return 'comment';
    }

    const firstChar = token[0];
    if (firstChar === '"' || firstChar === "'" || firstChar === '`') {
      return 'string';
    }

    if (/^\d/.test(token)) {
      return 'number';
    }

    return 'keyword';
  }
);

const highlightCss = (value: string): string => highlightByPattern(
  value,
  CSS_TOKEN_PATTERN,
  (token) => {
    if (token.startsWith('/*')) {
      return 'comment';
    }

    const firstChar = token[0];
    if (firstChar === '"' || firstChar === "'") {
      return 'string';
    }

    if (token.startsWith('@') || token === '!important') {
      return 'keyword';
    }

    if (firstChar === '#' || /^\d/.test(token)) {
      return 'number';
    }

    if (/^[{}:;(),]$/.test(token)) {
      return 'punctuation';
    }

    return 'property';
  }
);

const highlightHtmlAttributes = (rawAttributes: string): string => {
  const attributePattern = /(\s+)([^\s=/>]+)(\s*=\s*(?:"[^"]*"|'[^']*'|[^\s"'=<>`]+))?/g;
  let highlighted = '';
  let currentIndex = 0;
  attributePattern.lastIndex = 0;

  let match = attributePattern.exec(rawAttributes);

  while (match) {
    const [fullMatch, leadingWhitespace, attributeName, assignment = ''] = match;
    const index = match.index;

    highlighted += escapeHtml(rawAttributes.slice(currentIndex, index));
    highlighted += escapeHtml(leadingWhitespace);
    highlighted += wrapToken(attributeName, 'attrName');

    if (assignment) {
      const equalsIndex = assignment.indexOf('=');
      if (equalsIndex === -1) {
        highlighted += escapeHtml(assignment);
      } else {
        const leftSide = assignment.slice(0, equalsIndex);
        const rightSide = assignment.slice(equalsIndex + 1);

        highlighted += escapeHtml(leftSide);
        highlighted += wrapToken('=', 'punctuation');

        const rightTrimmed = rightSide.trimStart();
        const leadingRightWhitespace = rightSide.slice(0, rightSide.length - rightTrimmed.length);

        highlighted += escapeHtml(leadingRightWhitespace);
        highlighted += wrapToken(rightTrimmed, 'string');
      }
    }

    currentIndex = index + fullMatch.length;
    match = attributePattern.exec(rawAttributes);
  }

  highlighted += escapeHtml(rawAttributes.slice(currentIndex));
  return highlighted;
};

const highlightHtmlTag = (rawTag: string): string => {
  if (/^<!--[\s\S]*-->$/.test(rawTag)) {
    return wrapToken(rawTag, 'comment');
  }

  const match = rawTag.match(/^<(\/)?([a-zA-Z][\w:-]*)([\s\S]*?)(\/?)>$/);

  if (!match) {
    return wrapToken(rawTag, 'tagName');
  }

  const [, isClosing, tagName, rawAttributes, selfClosingSlash] = match;

  return [
    wrapToken('<', 'punctuation'),
    isClosing ? wrapToken('/', 'punctuation') : '',
    wrapToken(tagName, 'tagName'),
    highlightHtmlAttributes(rawAttributes),
    selfClosingSlash ? wrapToken('/', 'punctuation') : '',
    wrapToken('>', 'punctuation')
  ].join('');
};

const highlightHtmlSegment = (value: string): string => {
  let highlighted = '';
  let currentIndex = 0;
  HTML_TOKEN_PATTERN.lastIndex = 0;

  let match = HTML_TOKEN_PATTERN.exec(value);

  while (match) {
    const [token] = match;
    const index = match.index;

    highlighted += escapeHtml(value.slice(currentIndex, index));
    highlighted += highlightHtmlTag(token);

    currentIndex = index + token.length;
    match = HTML_TOKEN_PATTERN.exec(value);
  }

  highlighted += escapeHtml(value.slice(currentIndex));
  return highlighted;
};

const highlightHtmlMixed = (value: string): string => {
  let highlighted = '';
  let currentIndex = 0;
  SCRIPT_BLOCK_PATTERN.lastIndex = 0;

  let match = SCRIPT_BLOCK_PATTERN.exec(value);

  while (match) {
    const [scriptBlock] = match;
    const scriptStartIndex = match.index;

    highlighted += highlightHtmlSegment(value.slice(currentIndex, scriptStartIndex));

    const openTagMatch = scriptBlock.match(/^<script\b[^>]*>/i);
    const closeTagMatch = scriptBlock.match(/<\/script\s*>$/i);

    if (!openTagMatch || !closeTagMatch) {
      highlighted += highlightHtmlSegment(scriptBlock);
      currentIndex = scriptStartIndex + scriptBlock.length;
      match = SCRIPT_BLOCK_PATTERN.exec(value);
      continue;
    }

    const openTag = openTagMatch[0];
    const closeTag = closeTagMatch[0];
    const scriptBody = scriptBlock.slice(openTag.length, scriptBlock.length - closeTag.length);

    highlighted += highlightHtmlTag(openTag);
    highlighted += highlightJavaScript(scriptBody);
    highlighted += highlightHtmlTag(closeTag);

    currentIndex = scriptStartIndex + scriptBlock.length;
    match = SCRIPT_BLOCK_PATTERN.exec(value);
  }

  highlighted += highlightHtmlSegment(value.slice(currentIndex));
  return highlighted;
};

export const highlightCustomCodeContent = (value: string, mode: CustomCodeHighlightMode): string => {
  if (!value) {
    return '&nbsp;';
  }

  if (mode === 'css') {
    return highlightCss(value);
  }

  if (mode === 'js') {
    return highlightJavaScript(value);
  }

  return highlightHtmlMixed(value);
};
